import { create } from 'zustand';
import type { TradeApplicationDto, TradeApplicationStatus, TradeApplicationSummaryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';

/**
 * Trade applications (ADR-046): the queue, and the one open in front of staff.
 *
 * Every decision returns the whole application, so the open one re-renders
 * from what the server did; the queue is reloaded with it, since an answered
 * application leaves the waiting list.
 */

interface TradeApplicationsState {
  items: TradeApplicationSummaryDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  /** '' is every status. Waiting ones come first by default: they are the work. */
  status: TradeApplicationStatus | '';
  search: string;

  current: TradeApplicationDto | null;
  opening: boolean;
  deciding: boolean;
  decisionError: string | null;

  load: (filters?: { status?: TradeApplicationStatus | ''; search?: string }) => Promise<void>;
  loadMore: () => Promise<void>;
  open: (id: string) => Promise<void>;
  close: () => void;
  approve: (id: string, priceListId: string, note: string) => Promise<boolean>;
  requestChanges: (id: string, note: string) => Promise<boolean>;
  reject: (id: string, note: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useTradeApplicationsStore = create<TradeApplicationsState>((set, get) => {
  /** Runs a decision, shows its result, and refreshes the queue it moved in. */
  async function decide(run: () => Promise<TradeApplicationDto>): Promise<boolean> {
    set({ deciding: true, decisionError: null });
    try {
      const current = await withSession(run);
      set({ current, deciding: false });
      await get().load();
      return true;
    } catch (error) {
      set({ deciding: false, decisionError: errorMessage(error, 'Could not save the decision.') });
      return false;
    }
  }

  return {
    items: [],
    nextCursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    status: 'SUBMITTED',
    search: '',

    current: null,
    opening: false,
    deciding: false,
    decisionError: null,

    async load(filters) {
      const status = filters?.status ?? get().status;
      const search = filters?.search ?? get().search;
      set({ loading: true, error: null, status, search });
      try {
        const page = await withSession(() =>
          adminApi.tradeApplications.list({
            status: status || undefined,
            search: search || undefined,
            limit: PAGE_SIZE,
            locale: useUiStore.getState().locale,
          }),
        );
        set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
      } catch (error) {
        set({ loading: false, error: errorMessage(error, 'Could not load the applications.') });
      }
    },

    async loadMore() {
      const { nextCursor, loadingMore, status, search, items } = get();
      if (!nextCursor || loadingMore) return;
      set({ loadingMore: true });
      try {
        const page = await withSession(() =>
          adminApi.tradeApplications.list({
            status: status || undefined,
            search: search || undefined,
            limit: PAGE_SIZE,
            cursor: nextCursor,
            locale: useUiStore.getState().locale,
          }),
        );
        set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
      } catch (error) {
        set({ loadingMore: false, error: errorMessage(error, 'Could not load more applications.') });
      }
    },

    async open(id) {
      set({ opening: true, decisionError: null });
      try {
        // Read when opened: its documents' links last ten minutes.
        const current = await withSession(() => adminApi.tradeApplications.get(id, useUiStore.getState().locale));
        set({ current, opening: false });
      } catch (error) {
        set({ opening: false, error: errorMessage(error, 'Could not open the application.') });
      }
    },

    close() {
      set({ current: null, decisionError: null });
    },

    approve(id, priceListId, note) {
      return decide(() =>
        adminApi.tradeApplications.approve(id, { priceListId, note: note.trim() || null }, useUiStore.getState().locale),
      );
    },

    requestChanges(id, note) {
      return decide(() => adminApi.tradeApplications.requestChanges(id, note.trim(), useUiStore.getState().locale));
    },

    reject(id, note) {
      return decide(() => adminApi.tradeApplications.reject(id, note.trim(), useUiStore.getState().locale));
    },
  };
});
